/**
 * Purity Prayag Quiz Web Component
 * Implements personalized bundle recommendations based on user answers
 * Uses real bundle products from the store collection
 */

class Quiz extends HTMLElement {
  constructor() {
    super();
    this.currentQuestionIndex = 0;
    this.answers = {};
    this.partnerMapping = null;
    this.questionFlow = [];
    this.isInitialRender = true;
    this.bundleProducts = [];
    this.bundlesFetched = false;

    // Tag-based matching: maps quiz answer values to keywords found in product tags/titles/idealFor
    this.tagMapping = {
      // Q1: Who are you shopping for? → tag keywords to match
      q1Tags: {
        'myself': ['individual', 'personal', 'starter', 'clean eating'],
        'my_family': ['family', 'everyday', 'essentials', 'thali'],
        'my_parents': ['senior', 'wellness', 'elder', 'parent'],
        'my_children': ['kid', 'child', 'growth', 'strength'],
        'guests_events': ['guest', 'hosting', 'premium', 'gift', 'occasion']
      },
      // Q2: Cooking style → tag keywords
      q2Tags: {
        'daily_home_cooking': ['everyday', 'family', 'essentials', 'daily', 'thali'],
        'light_quick_meals': ['quick', 'convenience', 'light', 'easy', 'starter'],
        'health_focused': ['immunity', 'energy', 'weight', 'metabolism', 'wellness', 'health', 'heal'],
        'occasional_weekend': ['premium', 'guest', 'hosting', 'special']
      },
      // Q3: Health focus → tag keywords
      q3Tags: {
        'everyday_nutrition': ['everyday', 'family', 'essentials', 'nutrition', 'daily', 'goodness'],
        'energy_immunity': ['energy', 'immunity', 'boost'],
        'weight_metabolism': ['weight', 'metabolism', 'balance', 'millet'],
        'kids_growth': ['kid', 'child', 'growth', 'strength'],
        'senior_wellness': ['senior', 'wellness', 'elder', 'gentle']
      }
    };
  }

  connectedCallback() {
    this.init();
  }

  init() {
    // Parse real bundle products from JSON (rendered by Liquid)
    const bundleScript = this.querySelector('[data-bundle-products]');
    if (bundleScript) {
      try {
        this.bundleProducts = JSON.parse(bundleScript.textContent);
        this.bundlesFetched = true;
      } catch (e) {
        console.error('Failed to parse bundle products:', e);
        this.bundleProducts = [];
      }
    }

    // Get the collection handle for AJAX fallback
    this.bundlesCollectionHandle = this.dataset.bundlesCollection || '';

    // If no Liquid-rendered products, fetch via AJAX
    if (this.bundleProducts.length === 0 && this.bundlesCollectionHandle) {
      this.fetchBundleProductsAjax(this.bundlesCollectionHandle);
    }

    // Parse quiz data from JSON script
    const dataScript = this.querySelector('[data-quiz-data]');
    if (dataScript) {
      try {
        this.quizData = JSON.parse(dataScript.textContent);
      } catch (e) {
        console.error('Failed to parse quiz data:', e);
        return;
      }
    }

    // Initialize answers object
    this.initializeAnswers();

    // Cache DOM elements
    this.questionContainer = this.querySelector('.quiz-question-container');
    this.prevBtn = this.querySelector('.quiz-prev-btn');
    this.nextBtn = this.querySelector('.quiz-next-btn');
    this.progressIndicator = this.querySelector('.quiz-progress-indicator');

    // Store the original button HTML content (including icon)
    if (this.nextBtn && !this.nextBtn.hasAttribute('data-default-html')) {
      this.nextBtn.setAttribute('data-default-html', this.nextBtn.innerHTML);
    }

    // Bind event listeners
    this.prevBtn?.addEventListener('click', () => this.goToPrevious());
    this.nextBtn?.addEventListener('click', () => this.goToNext());

    // Initialize question flow
    this.questionFlow = [];
    if (this.quizData && this.quizData.questions) {
      this.quizData.questions.forEach((q, index) => {
        this.questionFlow.push(q.id || `q${index + 1}`);
      });
    }
    this.currentQuestionIndex = 0;

    // Render initial question
    this.renderQuestion();
    this.updateNavigation();
  }

  /**
   * AJAX fallback: fetch bundle products from /collections/{handle}/products.json
   */
  async fetchBundleProductsAjax(collectionHandle) {
    try {
      const response = await fetch(`/collections/${collectionHandle}/products.json?limit=20`);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();

      if (data.products && data.products.length > 0) {
        this.bundleProducts = data.products.map(p => ({
          id: p.id,
          title: p.title,
          handle: p.handle,
          url: `/products/${p.handle}`,
          price: p.variants[0]?.price ? Math.round(parseFloat(p.variants[0].price) * 100) : 0,
          priceFormatted: this.formatMoney(p.variants[0]?.price),
          compareAtPrice: p.variants[0]?.compare_at_price ? Math.round(parseFloat(p.variants[0].compare_at_price) * 100) : null,
          compareAtPriceFormatted: p.variants[0]?.compare_at_price ? this.formatMoney(p.variants[0].compare_at_price) : null,
          image: p.images[0]?.src || null,
          imageAlt: p.title,
          variantId: p.variants[0]?.id,
          available: p.variants[0]?.available !== false,
          description: p.body_html ? p.body_html.replace(/<[^>]*>/g, '').substring(0, 200) : '',
          tags: Array.isArray(p.tags) ? p.tags : (p.tags ? p.tags.split(', ') : []),
          idealFor: null,
          bundleContents: []
        }));
        this.bundlesFetched = true;
      }
    } catch (e) {
      console.error('Failed to fetch bundle products via AJAX:', e);
    }
  }

  /**
   * Format price string (e.g., "1228" → "Rs. 1,228.00")
   */
  formatMoney(price) {
    if (!price) return '';
    const amount = parseFloat(price);
    return `Rs. ${amount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }

  initializeAnswers() {
    this.answers = {
      q1: null,
      q2: null,
      q3: null,
      q4: null,
      q5: null
    };
  }

  getCurrentQuestionId() {
    return this.questionFlow[this.currentQuestionIndex];
  }

  renderQuestion() {
    const questionId = this.getCurrentQuestionId();
    if (!questionId) return;

    const questionData = this.quizData.questions.find(q => q.id === questionId);
    if (!questionData) return;

    // Update image display
    this.updateQuestionImage(questionData);

    // Clear previous question
    if (this.questionContainer) {
      this.questionContainer.innerHTML = '';
    }

    // Build question HTML
    const questionHTML = this.buildQuestionHTML(questionId, questionData);
    if (this.questionContainer) {
      this.questionContainer.innerHTML = questionHTML;
    }

    // Attach event listeners
    this.attachQuestionListeners(questionId, questionData);

    // Update progress
    this.updateProgress();

    // Scroll to top (but not on initial page load)
    if (!this.isInitialRender) {
      this.scrollToTop();
    } else {
      this.isInitialRender = false;
    }
  }

  updateQuestionImage(questionData) {
    const imageWrappers = this.querySelectorAll('.quiz-image-wrapper');

    if (imageWrappers.length === 0) return;

    imageWrappers.forEach(wrapper => {
      wrapper.classList.remove('active');
    });

    const imageIndex = questionData.imageIndex !== undefined ? questionData.imageIndex : this.currentQuestionIndex;
    const currentImageWrapper = this.querySelector(`[data-question-index="${imageIndex}"]`);

    if (currentImageWrapper) {
      currentImageWrapper.classList.add('active');

      if (questionData.imageBackgroundColor) {
        currentImageWrapper.style.backgroundColor = questionData.imageBackgroundColor;
      }
    }
  }

  buildQuestionHTML(questionId, questionData) {
    let html = '';

    html += `
      <div class="quiz-question-header">
        <div class="quiz-question-number">${questionData.number}</div>
        <div class="quiz-question-title">${questionData.title}</div>
      </div>
    `;

    html += `<div class="quiz-question-text">${questionData.text}</div>`;
    html += this.buildOptionsHTML(questionId, questionData);

    if (questionData.whyIAsk) {
      html += `
        <div class="quiz-why-i-ask">
          <strong>${this.quizData.whyIAskLabel || 'Why we ask:'}</strong> ${questionData.whyIAsk}
        </div>
      `;
    }

    return html;
  }

  buildOptionsHTML(questionId, questionData) {
    let html = '<div class="quiz-options-list">';
    html += '<div class="quiz-partner-group">';
    html += '<div class="quiz-partner-options">';

    const selectType = questionData.selectType || 'single';

    questionData.options.forEach((option) => {
      const currentAnswer = this.answers[questionId];
      let isSelected = false;

      if (selectType === 'multi') {
        isSelected = Array.isArray(currentAnswer) && currentAnswer.includes(option.value);
      } else {
        isSelected = currentAnswer === option.value;
      }

      html += `
        <div class="quiz-option ${isSelected ? 'selected' : ''}"
             data-option-value="${option.value}"
             data-select-type="${selectType}">
          <div class="quiz-option-text">${option.label}</div>
        </div>
      `;
    });

    html += '</div></div></div>';
    return html;
  }

  attachQuestionListeners(questionId, questionData) {
    const options = this.questionContainer.querySelectorAll('.quiz-option[data-option-value]');

    options.forEach(option => {
      option.addEventListener('click', () => {
        this.handleOptionClick(questionId, option, questionData);
      });
    });
  }

  handleOptionClick(questionId, optionElement, questionData) {
    const optionValue = optionElement.dataset.optionValue;
    const selectType = optionElement.dataset.selectType || 'single';
    const isMultiSelect = selectType === 'multi';

    if (isMultiSelect) {
      if (!Array.isArray(this.answers[questionId])) {
        this.answers[questionId] = [];
      }

      const index = this.answers[questionId].indexOf(optionValue);

      if (index > -1) {
        this.answers[questionId].splice(index, 1);
        optionElement.classList.remove('selected');
      } else {
        this.answers[questionId].push(optionValue);
        optionElement.classList.add('selected');
      }
    } else {
      this.questionContainer.querySelectorAll('.quiz-option').forEach(opt => {
        opt.classList.remove('selected');
      });

      optionElement.classList.add('selected');
      this.answers[questionId] = optionValue;

      if (questionId === 'q1') {
        this.partnerMapping = optionValue;
      }

      setTimeout(() => {
        this.goToNext();
      }, 300);
    }

    this.updateNavigation();
  }

  updateProgress() {
    const totalQuestions = this.questionFlow.length;
    const currentQuestion = this.currentQuestionIndex + 1;

    if (this.progressIndicator) {
      this.progressIndicator.textContent = `${currentQuestion}/${totalQuestions}`;
    }
  }

  updateNavigation() {
    if (this.prevBtn) {
      this.prevBtn.disabled = this.currentQuestionIndex === 0;
    }

    if (this.nextBtn) {
      const questionId = this.getCurrentQuestionId();
      const isLastQuestion = this.currentQuestionIndex === this.questionFlow.length - 1;

      if (isLastQuestion) {
        this.nextBtn.innerHTML = this.quizData.finishText || 'Finish';
        this.nextBtn.classList.add('finish');
      } else {
        const defaultButtonHTML = this.nextBtn.getAttribute('data-default-html');
        if (defaultButtonHTML) {
          this.nextBtn.innerHTML = defaultButtonHTML;
        }
        this.nextBtn.classList.remove('finish');
      }

      const isAnswered = this.isQuestionAnswered(questionId);
      this.nextBtn.disabled = !isAnswered;
    }
  }

  isQuestionAnswered(questionId) {
    const answer = this.answers[questionId];

    if (answer === null || answer === undefined) {
      return false;
    }

    if (Array.isArray(answer)) {
      return answer.length > 0;
    }

    return true;
  }

  goToPrevious() {
    if (this.currentQuestionIndex > 0) {
      this.currentQuestionIndex--;
      this.renderQuestion();
      this.updateNavigation();
    }
  }

  goToNext() {
    const questionId = this.getCurrentQuestionId();
    const isLastQuestion = this.currentQuestionIndex === this.questionFlow.length - 1;

    if (!this.isQuestionAnswered(questionId)) {
      return;
    }

    if (isLastQuestion) {
      this.finishQuiz();
    } else {
      this.currentQuestionIndex++;
      this.renderQuestion();
      this.updateNavigation();
    }
  }

  finishQuiz() {
    if (this.quizData.resultsUrl && !this.quizData.showResultsInline) {
      this.redirectToResults();
    } else {
      this.showResults();
    }
  }

  redirectToResults() {
    const url = new URL(this.quizData.resultsUrl, window.location.origin);

    Object.keys(this.answers).forEach(questionId => {
      const answer = this.answers[questionId];
      if (answer !== null && answer !== undefined) {
        if (Array.isArray(answer)) {
          url.searchParams.append(questionId, answer.join(','));
        } else {
          url.searchParams.append(questionId, answer);
        }
      }
    });

    window.location.href = url.toString();
  }

  showResults() {
    // Hide quiz container
    const quizContainer = this.querySelector('.quiz-container');
    if (quizContainer) {
      quizContainer.style.display = 'none';
    }

    // If skip_customer_form is enabled, show recommendations directly
    if (this.quizData.skipCustomerForm) {
      this.showRecommendationsDirectly();
    } else {
      // Show customer form first — recommendations appear after submission
      this.showCustomerForm();
    }
  }

  /**
   * Show recommendations directly without the customer form
   */
  showRecommendationsDirectly() {
    const existing = this.querySelector('.quiz-results-container');
    if (existing) existing.remove();

    const resultsContainer = document.createElement('div');
    resultsContainer.className = 'quiz-results-container';
    resultsContainer.innerHTML = this.buildResultsHTML();

    const customerForm = this.querySelector('.quiz-customer-form');
    if (customerForm && customerForm.parentNode) {
      customerForm.parentNode.insertBefore(resultsContainer, customerForm);
    } else {
      this.appendChild(resultsContainer);
    }

    this.bindAddToCartHandlers(resultsContainer);

    setTimeout(() => {
      resultsContainer.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  }

  /**
   * Show bundle recommendations first, then the customer form below
   */
  showRecommendationsAndForm() {
    // Remove any existing results container
    const existing = this.querySelector('.quiz-results-container');
    if (existing) existing.remove();

    // Build and insert results
    const resultsContainer = document.createElement('div');
    resultsContainer.className = 'quiz-results-container';
    resultsContainer.innerHTML = this.buildResultsHTML();

    // Insert before the customer form
    const customerForm = this.querySelector('.quiz-customer-form');
    if (customerForm && customerForm.parentNode) {
      customerForm.parentNode.insertBefore(resultsContainer, customerForm);
    } else {
      this.appendChild(resultsContainer);
    }

    // Bind add-to-cart handlers
    this.bindAddToCartHandlers(resultsContainer);

    // Now show the customer form below the results
    this.showCustomerForm();

    // Scroll to results
    setTimeout(() => {
      resultsContainer.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  }

  showCustomerForm() {
    const customerForm = this.querySelector('.quiz-customer-form');
    if (!customerForm) {
      console.error('Customer form not found');
      return;
    }

    this.updateQuizDataInNote();
    this.updateQuizTags();

    customerForm.classList.add('quiz-form-visible');
    customerForm.style.display = 'block';

    // Use click handler on button (no native <form> to cause page refresh)
    const submitButton = customerForm.querySelector('.quiz-form-submit');
    if (submitButton) {
      submitButton.addEventListener('click', async () => {
        const phoneInput = this.querySelector('#QuizForm-phone');
        const phoneCountrySelect = this.querySelector('#QuizForm-phone-country');
        const firstNameInput = this.querySelector('#QuizForm-FirstName');
        const errorMessages = this.quizData.errorMessages || {};

        // Build full phone number with country code
        let fullPhone = '';
        if (phoneInput && phoneCountrySelect) {
          const phoneNumber = phoneInput.value.trim().replace(/^0+/, '');
          const countryCode = phoneCountrySelect.value;
          if (phoneNumber) {
            fullPhone = `${countryCode}${phoneNumber}`;
          }
        }

        // Validate required fields
        if (!firstNameInput?.value.trim()) {
          alert(errorMessages.firstNameRequired || 'Please enter your first name');
          firstNameInput?.focus();
          return;
        }
        if (!fullPhone) {
          alert(errorMessages.phoneRequired || 'Please enter your phone number');
          phoneInput?.focus();
          return;
        }

        submitButton.disabled = true;
        submitButton.textContent = this.quizData.submittingText || 'Getting your recommendations...';

        const firstName = firstNameInput.value.trim();

        // Update tags with customer info (tags ARE saved to customer record)
        this.updateQuizTags(fullPhone, firstName);

        // Step 1: Submit newsletter form (creates customer in Shopify with email + tags)
        try {
          const tagsField = this.querySelector('#QuizForm-quiz-tags');
          const newsletterData = new FormData();
          newsletterData.append('form_type', 'customer');
          newsletterData.append('utf8', '\u2713');
          newsletterData.append('contact[email]', `${fullPhone.replace(/[^0-9]/g, '')}@quiz.purityprayag.com`);
          newsletterData.append('contact[tags]', tagsField?.value || 'quiz-submission');
          newsletterData.append('customer[accepts_marketing]', 'true');

          await fetch('/contact', {
            method: 'POST',
            body: newsletterData,
            headers: { 'Accept': 'text/html' }
          });
        } catch (err) {
          console.error('Newsletter form submission error:', err);
        }

        // Step 2: Submit contact form (sends email notification with ALL details to store owner)
        try {
          const recommendedBundles = this.calculateRecommendedBundles();
          const bundleNames = recommendedBundles.map(b => b.title).join(', ');
          const quizAnswersSummary = Object.entries(this.answers)
            .map(([q, a]) => `${q}: ${Array.isArray(a) ? a.join(', ') : a}`)
            .join('\n');

          const contactBody = [
            `Name: ${firstName}`,
            `Phone: ${fullPhone}`,
            ``,
            `Quiz Answers:`,
            quizAnswersSummary,
            ``,
            `Recommended Bundles: ${bundleNames}`,
            `Submitted: ${new Date().toLocaleString()}`
          ].join('\n');

          const contactData = new FormData();
          contactData.append('form_type', 'contact');
          contactData.append('utf8', '\u2713');
          contactData.append('contact[email]', `${fullPhone.replace(/[^0-9]/g, '')}@quiz.purityprayag.com`);
          contactData.append('contact[first_name]', firstName);
          contactData.append('contact[phone]', fullPhone);
          contactData.append('contact[body]', contactBody);
          contactData.append('contact[tags]', 'quiz-submission');

          await fetch('/contact#contact_form', {
            method: 'POST',
            body: contactData,
            headers: { 'Accept': 'text/html' }
          });
        } catch (err) {
          console.error('Contact form submission error:', err);
        }

        // Step 3: Add recommended bundles to cart (creates abandoned checkout)
        const recommendedBundles = this.calculateRecommendedBundles();
        const cartItems = recommendedBundles
          .filter(b => b.available !== false && b.variantId)
          .map(b => ({ id: parseInt(b.variantId, 10), quantity: 1 }));

        if (cartItems.length > 0) {
          try {
            await fetch('/cart/clear.js', { method: 'POST', headers: { 'Content-Type': 'application/json' } });
            await fetch('/cart/add.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ items: cartItems })
            });
          } catch (err) {
            console.error('Cart add error:', err);
          }
        }

        // Step 4: Hide form and show recommendations
        customerForm.style.display = 'none';
        this.showRecommendationsAfterSubmit();
      });
    }
  }

  /**
   * Show thank-you message + bundle recommendations after form submission
   */
  showRecommendationsAfterSubmit() {
    // Remove any existing results
    const existing = this.querySelector('.quiz-results-container');
    if (existing) existing.remove();
    const existingThankYou = this.querySelector('.quiz-thank-you-container');
    if (existingThankYou) existingThankYou.remove();

    // Build thank-you message
    const thankYouContainer = document.createElement('div');
    thankYouContainer.className = 'quiz-thank-you-container';
    thankYouContainer.innerHTML = `
      <div class="quiz-thank-you">
        <div class="quiz-thank-you-icon">
          <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="24" cy="24" r="24" fill="#1a5c3a" opacity="0.1"/>
            <path d="M15 25L21 31L33 19" stroke="#1a5c3a" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </div>
        <h2 class="quiz-thank-you-title">${this.quizData?.successMessage || 'Thank you! Your personalized recommendations are ready.'}</h2>
        <p class="quiz-thank-you-subtitle">${this.quizData?.successSubmessage || 'Based on your answers, we\'ve curated the perfect bundles for your home.'}</p>
      </div>
    `;

    // Build results
    const resultsContainer = document.createElement('div');
    resultsContainer.className = 'quiz-results-container';
    resultsContainer.innerHTML = this.buildResultsHTML();

    // Insert into page
    const customerForm = this.querySelector('.quiz-customer-form');
    if (customerForm && customerForm.parentNode) {
      customerForm.parentNode.insertBefore(thankYouContainer, customerForm);
      customerForm.parentNode.insertBefore(resultsContainer, customerForm);
    } else {
      this.appendChild(thankYouContainer);
      this.appendChild(resultsContainer);
    }

    // Bind add-to-cart handlers
    this.bindAddToCartHandlers(resultsContainer);

    // Scroll to thank-you
    setTimeout(() => {
      thankYouContainer.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  }

  showThankYouAfterSubmit() {
    // Hide the form and show a thank-you message
    const customerForm = this.querySelector('.quiz-customer-form');
    if (customerForm) customerForm.style.display = 'none';

    const thankYou = document.createElement('div');
    thankYou.className = 'quiz-thank-you-container';
    thankYou.innerHTML = `
      <div class="quiz-thank-you">
        <div class="quiz-thank-you-icon">
          <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="24" cy="24" r="24" fill="#1a5c3a" opacity="0.1"/>
            <path d="M15 25L21 31L33 19" stroke="#1a5c3a" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </div>
        <h2 class="quiz-thank-you-title">${this.quizData?.successMessage || 'Thank you! Your recommendations have been saved.'}</h2>
        <p class="quiz-thank-you-subtitle">${this.quizData?.successSubmessage || 'We\'ll contact you with your personalized bundle picks.'}</p>
      </div>
    `;

    if (customerForm && customerForm.parentNode) {
      customerForm.parentNode.insertBefore(thankYou, customerForm);
    } else {
      this.appendChild(thankYou);
    }

    thankYou.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  updateQuizDataInNote() {
    // No-op: quiz data is now sent via contact form body in the submit handler
  }

  updateQuizTags(phone, firstName) {
    const tagsField = this.querySelector('#QuizForm-quiz-tags');
    const customerTagsField = this.querySelector('#QuizForm-quiz-tags-customer');

    const tags = ['quiz-submission'];

    // Include customer contact info in tags (these ARE saved to the customer record)
    if (phone) tags.push(`phone:${phone}`);
    if (firstName) tags.push(`fname:${firstName}`);

    // Include location if available
    const userCity = (localStorage.getItem('hita-location-city') || '').toLowerCase();
    if (userCity) tags.push(`city:${userCity}`);

    // Include quiz answer tags
    Object.keys(this.answers).forEach(questionId => {
      const answer = this.answers[questionId];
      if (answer) {
        if (Array.isArray(answer)) {
          answer.forEach(v => tags.push(`${questionId}:${v}`));
        } else {
          tags.push(`${questionId}:${answer}`);
        }
      }
    });

    const recommendedBundles = this.calculateRecommendedBundles();
    recommendedBundles.forEach(b => {
      tags.push(`recommended-bundle:${b.handle}`);
    });

    const tagsString = tags.join(', ');

    if (tagsField) tagsField.value = tagsString;
    if (customerTagsField) customerTagsField.value = tagsString;
  }

  /**
   * Get searchable text for a product (tags + title + idealFor combined and lowercased)
   */
  getProductSearchText(product) {
    const parts = [];
    if (product.tags && Array.isArray(product.tags)) {
      parts.push(...product.tags.map(t => t.toLowerCase()));
    }
    if (product.title) {
      parts.push(product.title.toLowerCase());
    }
    if (product.idealFor) {
      parts.push(product.idealFor.toLowerCase());
    }
    if (product.description) {
      parts.push(product.description.toLowerCase());
    }
    return parts.join(' ');
  }

  /**
   * Score a product against a list of keywords. Returns how many keywords match.
   */
  scoreProductAgainstKeywords(product, keywords) {
    if (!keywords || keywords.length === 0) return 0;
    const searchText = this.getProductSearchText(product);
    let score = 0;
    for (const keyword of keywords) {
      if (searchText.includes(keyword.toLowerCase())) {
        score++;
      }
    }
    return score;
  }

  /**
   * Calculate recommended bundles from real products based on quiz answers
   */
  calculateRecommendedBundles() {
    if (!this.bundleProducts || this.bundleProducts.length === 0) {
      return [];
    }

    const q1 = this.answers.q1;
    const q2 = this.answers.q2;
    const q3 = this.answers.q3;
    const q4 = this.answers.q4;
    const q5 = this.answers.q5;

    const MIN_RESULTS = 3;

    // Get user's selected location for location-based filtering
    const userCity = (localStorage.getItem('hita-location-city') || '').toLowerCase();

    // Filter: exclude sold-out products and products not available in user's location
    const availableProducts = this.bundleProducts.filter(product => {
      // Exclude sold-out products
      if (product.available === false) return false;

      // Location-based filtering: if user has a city selected, check for available:{city} tag
      if (userCity) {
        const productTags = (product.tags || []).map(t => t.toLowerCase());
        const hasLocationTags = productTags.some(t => t.startsWith('available:'));
        if (hasLocationTags) {
          // Products tagged available:all show everywhere
          const isAvailableAll = productTags.includes('available:all');
          const isAvailableInCity = productTags.includes(`available:${userCity}`);
          if (!isAvailableAll && !isAvailableInCity) return false;
        }
      }

      return true;
    });

    // Score each available product
    const scored = availableProducts.map(product => {
      let score = 0;

      // Q1 matching (who shopping for) — highest weight
      const q1Keywords = this.tagMapping.q1Tags[q1] || [];
      score += this.scoreProductAgainstKeywords(product, q1Keywords) * 5;

      // Q2 matching (cooking style)
      const q2Keywords = this.tagMapping.q2Tags[q2] || [];
      score += this.scoreProductAgainstKeywords(product, q2Keywords) * 3;

      // Q3 matching (health focus) — high weight
      const q3Keywords = this.tagMapping.q3Tags[q3] || [];
      score += this.scoreProductAgainstKeywords(product, q3Keywords) * 4;

      // Q4: brand preference — boost Purity Prayag tagged products
      if (q4 === 'purity_prayag_only') {
        const searchText = this.getProductSearchText(product);
        if (searchText.includes('purity prayag') || searchText.includes('purity-prayag')) {
          score += 3;
        }
      }

      // Q5: budget filtering — boost products in budget range
      if (q5 && product.price) {
        const priceInRupees = product.price / 100;
        if (q5 === 'budget_1000_1500' && priceInRupees <= 1500) {
          score += 2;
        } else if (q5 === 'budget_1500_3000' && priceInRupees >= 1500 && priceInRupees <= 3000) {
          score += 2;
        } else if (q5 === 'budget_3000_plus' && priceInRupees >= 3000) {
          score += 2;
        }
      }

      return { ...product, score, whyShowing: this.getPersonalizedWhyShowing(q1, q2, q3) };
    });

    // Sort by score descending, then by price ascending as tiebreaker
    scored.sort((a, b) => {
      if (b.score !== a.score) return b.score - a.score;
      return (a.price || 0) - (b.price || 0);
    });

    // Always return at least MIN_RESULTS (3) recommendations
    const results = scored.slice(0, Math.max(MIN_RESULTS, 3));

    // Mark the first one as primary (best match)
    if (results.length > 0) {
      results[0].isPrimary = true;
    }

    return results;
  }

  getPersonalizedWhyShowing(q1, q2, q3) {
    const shoppingForLabels = {
      'myself': 'you',
      'my_family': 'your family',
      'my_parents': 'your parents',
      'my_children': 'your children',
      'guests_events': 'your guests'
    };

    const cookingStyleLabels = {
      'daily_home_cooking': 'daily cooking routine',
      'light_quick_meals': 'quick meal preferences',
      'health_focused': 'health-focused lifestyle',
      'occasional_weekend': 'weekend cooking style'
    };

    const healthFocusLabels = {
      'everyday_nutrition': 'everyday nutrition',
      'energy_immunity': 'energy & immunity goals',
      'weight_metabolism': 'weight & metabolism focus',
      'kids_growth': 'growth & development needs',
      'senior_wellness': 'wellness requirements'
    };

    const target = shoppingForLabels[q1] || 'you';
    const cooking = cookingStyleLabels[q2] || '';
    const health = healthFocusLabels[q3] || '';

    return `Selected for ${target} based on your ${cooking} and ${health}.`;
  }

  buildResultsHTML() {
    const recommendedBundles = this.calculateRecommendedBundles();
    const shoppingForLabel = this.getShoppingForLabel(this.answers.q1);
    const targetLabel = this.getTargetLabel(this.answers.q1);

    // If no bundle products are configured, show a fallback with browse link
    if (recommendedBundles.length === 0) {
      const browseUrl = this.bundlesCollectionHandle
        ? `/collections/${this.bundlesCollectionHandle}`
        : '/collections/all';

      return `
        <div class="quiz-results-header">
          <h2>Your everyday food match for ${targetLabel}</h2>
          <p class="quiz-results-subtitle">
            Based on your answers, we're preparing personalized bundle recommendations for you.
          </p>
        </div>
        <div class="quiz-results-summary">
          <div class="quiz-results-summary-item">
            <span class="quiz-summary-label">Shopping for:</span>
            <span class="quiz-summary-value">${shoppingForLabel}</span>
          </div>
          <div class="quiz-results-summary-item">
            <span class="quiz-summary-label">Cooking style:</span>
            <span class="quiz-summary-value">${this.getCookingStyleLabel(this.answers.q2)}</span>
          </div>
          <div class="quiz-results-summary-item">
            <span class="quiz-summary-label">Health focus:</span>
            <span class="quiz-summary-value">${this.getHealthFocusLabel(this.answers.q3)}</span>
          </div>
        </div>
        <div class="quiz-no-bundles">
          <p>Our team will review your preferences and suggest the best bundles for ${targetLabel.toLowerCase()}.</p>
          <a href="${browseUrl}" class="quiz-bundle-cta primary-cta">Browse All Bundles</a>
        </div>
      `;
    }

    let html = `
      <div class="quiz-results-header">
        <h2>Your everyday food match for ${targetLabel}</h2>
        <p class="quiz-results-subtitle">
          Based on your cooking habits, health focus, and budget comfort — here are the bundles that fit your home best.
        </p>
      </div>

      <div class="quiz-results-summary">
        <div class="quiz-results-summary-item">
          <span class="quiz-summary-label">Shopping for:</span>
          <span class="quiz-summary-value">${shoppingForLabel}</span>
        </div>
        <div class="quiz-results-summary-item">
          <span class="quiz-summary-label">Cooking style:</span>
          <span class="quiz-summary-value">${this.getCookingStyleLabel(this.answers.q2)}</span>
        </div>
        <div class="quiz-results-summary-item">
          <span class="quiz-summary-label">Health focus:</span>
          <span class="quiz-summary-value">${this.getHealthFocusLabel(this.answers.q3)}</span>
        </div>
      </div>

      <div class="quiz-results-bundles">
        <h3>Recommended Bundles for You</h3>
        <div class="quiz-bundles-grid">
    `;

    recommendedBundles.forEach((bundle, index) => {
      const hasComparePrice = bundle.compareAtPrice && bundle.compareAtPrice > bundle.price;
      const savingsPercent = hasComparePrice
        ? Math.round(((bundle.compareAtPrice - bundle.price) / bundle.compareAtPrice) * 100)
        : 0;

      const matchLabel = bundle.isPrimary ? 'Best Match' : `Match #${index + 1}`;

      html += `
        <div class="quiz-bundle-card ${bundle.isPrimary ? 'primary' : ''}" style="animation-delay: ${index * 0.15}s">
          <div class="quiz-bundle-badge ${bundle.isPrimary ? 'best' : ''}">${matchLabel}</div>

          ${bundle.image ? `
            <a href="${bundle.url}" class="quiz-bundle-image-link">
              <div class="quiz-bundle-image-wrapper">
                <img src="${bundle.image}" alt="${bundle.imageAlt || bundle.title}" class="quiz-bundle-image" loading="lazy" width="400" height="400">
              </div>
            </a>
          ` : ''}

          <div class="quiz-bundle-body">
            <a href="${bundle.url}" class="quiz-bundle-name-link">
              <h4 class="quiz-bundle-name">${bundle.title}</h4>
            </a>

            <div class="quiz-bundle-pricing">
              <span class="quiz-bundle-price">${bundle.priceFormatted}</span>
              ${hasComparePrice ? `
                <span class="quiz-bundle-compare-price">${bundle.compareAtPriceFormatted}</span>
                <span class="quiz-bundle-savings">Save ${savingsPercent}%</span>
              ` : ''}
            </div>

            ${bundle.idealFor ? `<p class="quiz-bundle-ideal-for">Ideal for: ${bundle.idealFor}</p>` : ''}

            <p class="quiz-bundle-why">${bundle.whyShowing}</p>

            ${bundle.bundleContents && bundle.bundleContents.length > 0 ? `
              <div class="quiz-bundle-contents expandable" data-expanded="false">
                <button class="quiz-bundle-contents-toggle" onclick="this.parentElement.dataset.expanded = this.parentElement.dataset.expanded === 'true' ? 'false' : 'true'">
                  <span class="toggle-text">What's inside (${bundle.bundleContents.length} items)</span>
                  <span class="quiz-toggle-icon">
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M2 4L6 8L10 4" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                  </span>
                </button>
                <ul class="quiz-bundle-sku-list">
                  ${bundle.bundleContents.map(item => `<li>${item}</li>`).join('')}
                </ul>
              </div>
            ` : (bundle.description ? `<p class="quiz-bundle-description">${bundle.description}</p>` : '')}

            <div class="quiz-bundle-actions">
              <button class="quiz-bundle-cta primary-cta"
                      data-variant-id="${bundle.variantId}"
                      data-product-url="${bundle.url}"
                      ${!bundle.available ? 'disabled' : ''}>
                ${bundle.available ? 'Add to Cart' : 'Sold Out'}
              </button>
              <a href="${bundle.url}" class="quiz-bundle-cta secondary-cta">View Details</a>
            </div>
          </div>
        </div>
      `;
    });

    html += `
        </div>
      </div>

      <div class="quiz-results-actions">
        <button onclick="location.reload()" class="quiz-action-link secondary">
          Retake Quiz
        </button>
      </div>
    `;

    return html;
  }

  /**
   * Bind add-to-cart click handlers on result bundle cards
   */
  bindAddToCartHandlers(container) {
    const buttons = container.querySelectorAll('.quiz-bundle-cta.primary-cta[data-variant-id]');
    buttons.forEach(btn => {
      btn.addEventListener('click', (e) => this.handleAddToCart(e));
    });
  }

  /**
   * AJAX add to cart for quiz recommended bundle products
   */
  async handleAddToCart(e) {
    const button = e.currentTarget;
    const variantId = button.dataset.variantId;
    if (!variantId) return;

    const originalText = button.textContent;
    button.disabled = true;
    button.textContent = 'Adding...';

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ id: parseInt(variantId, 10), quantity: 1 })
      });

      const data = await response.json();

      if (response.status >= 400) {
        throw data;
      }

      // Fetch updated cart
      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();

      // Dispatch cart update events for theme integration
      document.dispatchEvent(new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          resource: cart,
          sourceId: '',
          data: {
            source: 'quiz-recommendation',
            productId: data.product_id ? String(data.product_id) : '',
            variantId: String(variantId),
            itemCount: data.quantity || 1,
            sections: data.sections || undefined
          }
        }
      }));
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));

      button.textContent = 'Added!';
      setTimeout(() => {
        button.textContent = originalText;
        button.disabled = false;
      }, 1500);

    } catch (err) {
      console.error('Error adding to cart:', err);
      button.textContent = 'Error';
      setTimeout(() => {
        button.textContent = originalText;
        button.disabled = false;
      }, 2000);
    }
  }

  getTargetLabel(q1Value) {
    const targetLabels = {
      'myself': 'You',
      'my_family': 'Your Family',
      'my_parents': 'Your Parents',
      'my_children': 'Your Kids',
      'guests_events': 'Your Guests'
    };
    return targetLabels[q1Value] || 'You';
  }

  getShoppingForLabel(value) {
    const labels = {
      'myself': 'Myself',
      'my_family': 'My Family',
      'my_parents': 'My Parents',
      'my_children': 'My Children',
      'guests_events': 'Guests/Events'
    };
    return labels[value] || value;
  }

  getCookingStyleLabel(value) {
    const labels = {
      'daily_home_cooking': 'Daily home cooking',
      'light_quick_meals': 'Light & quick meals',
      'health_focused': 'Health-focused cooking',
      'occasional_weekend': 'Occasional / weekend cooking'
    };
    return labels[value] || value;
  }

  getHealthFocusLabel(value) {
    const labels = {
      'everyday_nutrition': 'Everyday nutrition',
      'energy_immunity': 'Energy & immunity',
      'weight_metabolism': 'Weight & metabolism',
      'kids_growth': "Kids' growth",
      'senior_wellness': 'Senior wellness'
    };
    return labels[value] || value;
  }

  scrollToTop() {
    this.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
}

// Register the custom element
if (!customElements.get('grocery-quiz')) {
  customElements.define('grocery-quiz', Quiz);
}
